import React, { useState } from 'react';

interface AddJobModalProps {
  defaultStatus: string;
  onClose: () => void;
  onAdd: (jobData: {
    job_title: string;
    job_url: string;
    company_name: string;
    status: string;
  }) => Promise<void>;
}

const AddJobModal: React.FC<AddJobModalProps> = ({ defaultStatus, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    job_title: '',
    job_url: '',
    company_name: '',
    status: defaultStatus,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.job_title || !formData.job_url || !formData.company_name) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await onAdd(formData);
      onClose();
    } catch (err) {
      setError('Failed to add job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-jobesta shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-jobesta-gray-90">Add New Job</h2>
            <button
              onClick={onClose}
              className="text-jobesta-gray-60 hover:text-jobesta-gray-90"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="job_title" className="block text-sm font-medium text-jobesta-gray-70 mb-2">
                Job Title *
              </label>
              <input
                type="text"
                id="job_title"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-jobesta-gray-40 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-blue"
                placeholder="e.g., Software Engineer"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="company_name" className="block text-sm font-medium text-jobesta-gray-70 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-jobesta-gray-40 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-blue"
                placeholder="e.g., Google"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="job_url" className="block text-sm font-medium text-jobesta-gray-70 mb-2">
                Job URL *
              </label>
              <input
                type="url"
                id="job_url"
                name="job_url"
                value={formData.job_url}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-jobesta-gray-40 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-blue"
                placeholder="https://example.com/job/123"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="status" className="block text-sm font-medium text-jobesta-gray-70 mb-2">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-jobesta-gray-40 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-blue"
              >
                <option value="Viewed">Viewed</option>
                <option value="Applied">Applied</option>
                <option value="Interview">Interview</option>
                <option value="Offer">Offer</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-jobesta text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-jobesta-gray-40 text-jobesta-gray-70 rounded-jobesta hover:bg-jobesta-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-jobesta-blue text-white rounded-jobesta hover:bg-jobesta-darkblue transition-colors disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Job'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddJobModal;